// ./src/hooks/useEventPolicies.ts
//
// Loads an event's attached policies (active + disabled, so the compliance page
// can re-enable disabled ones), ordered, with the referenced policy detail +
// acceptance records. `reload` refetches after a change.

import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { EventPolicyService, EventPolicy } from "@/utils/api";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UseEventPoliciesReturn {
  eventPolicies: EventPolicy[];
  loading: boolean;
  /** True once the first fetch has completed (list may still be empty). */
  isLoaded: boolean;
  error: OpResult | null;
  reload: () => Promise<void>;
}

export const useEventPolicies = (eventId?: string | null): UseEventPoliciesReturn => {
  const [eventPolicies, setEventPolicies] = useState<EventPolicy[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);
  const callIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!eventId) {
      setEventPolicies([]);
      setIsLoaded(true);
      return;
    }
    const myCall = ++callIdRef.current;
    setLoading(true);
    setError(null);

    const result = await EventPolicyService.list(eventId, [STATUSES.ACTIVE, STATUSES.DISABLED]);

    if (myCall !== callIdRef.current) return;
    setLoading(false);
    setIsLoaded(true);

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      setEventPolicies(result.data as EventPolicy[]);
    } else {
      setError(result);
      setEventPolicies([]);
    }
  }, [eventId]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(load, isLoaded);

  useEffect(() => {
    load();
  }, [load]);

  return { eventPolicies, loading, isLoaded, error, reload: load };
};
